import { useEffect, useMemo, useState } from 'react';

import {
	Alert,
	Box,
	Button,
	Group,
	LoadingOverlay,
	Paper,
	Radio,
	ScrollArea,
	SimpleGrid,
	Stack,
	Text,
} from '@mantine/core';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { scudMappingApi } from '@/shared/api/scudMapping';
import { SCUD_COMMENT_MAX_LENGTH, SCUD_EMPTY } from '@/shared/constant/scudMapping';
import { useShowNotifaction } from '@/shared/hooks/useShowNotifaction';
import {
	IScudAuditEntry,
	IScudFederationPerson,
	IScudMappingDetails,
	TScudMappingAction,
} from '@/shared/types/scudMapping.types';
import { LabelInput } from '@/shared/uikit/components/input';
import { ModalSheet } from '@/shared/uikit/components/modalSheet';

import { IconUserSearch } from '@tabler/icons-react';

import { FederationSearchModal, InfoItem } from './FederationSearchModal';
import { ScudMappingHistory } from './ScudMappingHistory';
import { ScudStatusBadge } from './ScudStatusBadge';
import { SCUD_MAPPING_DETAILS_KEY, SCUD_MAPPING_LIST_KEY, SCUD_REASONS_ALL_KEY } from './queryKeys';
import {
	employeeDetails,
	formatScudDate,
	formatScudDateTime,
	isConflict,
	joinParts,
	reasonLabel,
	reasonLabelById,
	requestErrorText,
	scudPassNumber,
} from './utils';

type ScudMappingEditFormProps = {
	/** null — форма закрыта */
	mappingId: number | null;
	onClose: () => void;
};

const initialAction = (details: IScudMappingDetails): TScudMappingAction | null => {
	if (details.match_status === 1) {
		return 'found';
	}

	return details.match_status === 3 ? 'not_required' : null;
};

/** Когда в журнале последний раз появилось текущее значение поля — дата для «Было» */
const changedAt = (history: IScudAuditEntry[], key: string, value: unknown) =>
	history
		.filter((entry) => entry.new_values?.[key] === value)
		.reduce<
			string | null
		>((latest, entry) => (!latest || entry.changed_at > latest ? entry.changed_at : latest), null);

const MATCHED_BY_TEXT: Record<string, string> = {
	etl: 'сопоставила загрузка',
	guard: 'сопоставила охрана',
};

/** Текущий сотрудник записи; federation пуст, если в «Федерации» человека уже нет */
const initialEmployee = (details: IScudMappingDetails): IScudFederationPerson | null => {
	if (details.match_status !== 1 || !details.person_id_federation) {
		return null;
	}

	return (
		details.federation ?? {
			person_id: details.person_id_federation,
			full_name: details.full_name_federation ?? `id ${details.person_id_federation}`,
			is_main: true,
		}
	);
};

type OptionCardProps = { value: string; label: string; checked: boolean };

const OptionCard = ({ value, label, checked }: OptionCardProps) => (
	<Radio.Card
		value={value}
		radius="lg"
		px="sm"
		py={10}
		withBorder
		bg={checked ? 'var(--mantine-primary-color-light)' : undefined}
		style={checked ? { borderColor: 'var(--mantine-primary-color-filled)' } : undefined}
	>
		<Group gap="sm" wrap="nowrap">
			<Radio.Indicator />
			<Text size="sm" fw={checked ? 700 : 400}>
				{label}
			</Text>
		</Group>
	</Radio.Card>
);

export const ScudMappingEditForm = ({ mappingId, onClose }: ScudMappingEditFormProps) => {
	const queryClient = useQueryClient();
	const opened = mappingId !== null;
	// Последний id держим, чтобы содержимое не пропадало при закрытии; новую запись берём в том же рендере
	const [currentId, setCurrentId] = useState<number | null>(mappingId);

	if (mappingId !== null && mappingId !== currentId) {
		setCurrentId(mappingId);
	}
	const [action, setAction] = useState<TScudMappingAction | null>(null);
	const [employee, setEmployee] = useState<IScudFederationPerson | null>(null);
	const [reasonId, setReasonId] = useState<string | null>(null);
	const [comment, setComment] = useState('');
	const [isSearchOpened, setIsSearchOpened] = useState(false);

	const detailsKey = [...SCUD_MAPPING_DETAILS_KEY, currentId];

	const {
		data: details,
		isFetching,
		isError,
		error,
		refetch,
	} = useQuery({
		queryKey: detailsKey,
		queryFn: () => scudMappingApi.get(currentId as number),
		enabled: opened && currentId !== null,
		retry: false,
	});

	const {
		data: reasons = [],
		isPending: isReasonsPending,
		isError: isReasonsError,
	} = useQuery({
		queryKey: SCUD_REASONS_ALL_KEY,
		queryFn: () => scudMappingApi.getReasons(false),
		enabled: opened,
		staleTime: 300_000,
	});

	// Новая запись или новая версия после 409 — форма заново
	useEffect(() => {
		if (!details || !opened) {
			return;
		}

		setAction(initialAction(details));
		setEmployee(initialEmployee(details));
		setReasonId(details.reason_id ?? null);
		setComment(details.guard_comment ?? '');
		setIsSearchOpened(false);
	}, [details?.id, details?.version, opened]);

	const handleSaved = (text: string) => {
		void queryClient.invalidateQueries({ queryKey: SCUD_MAPPING_LIST_KEY });
		void queryClient.invalidateQueries({ queryKey: detailsKey });
		useShowNotifaction({ text, type: 'success' });
		onClose();
	};

	const handleError = (err: unknown) => {
		useShowNotifaction({
			text: requestErrorText(err, 'Не удалось сохранить запись'),
			type: 'error',
		});

		if (isConflict(err)) {
			void queryClient.invalidateQueries({ queryKey: SCUD_MAPPING_LIST_KEY });
			void refetch();
		}
	};

	const { mutate: confirm, isPending: isConfirming } = useMutation({
		mutationFn: scudMappingApi.confirm,
		onSuccess: () => handleSaved('Запись подтверждена'),
		onError: handleError,
	});

	const { mutate: update, isPending: isUpdating } = useMutation({
		mutationFn: scudMappingApi.update,
		onSuccess: (_, { payload }) =>
			handleSaved(
				payload.action === 'found'
					? 'Сотрудник сопоставлен, запись подтверждена'
					: 'Запись подтверждена: сопоставление не требуется',
			),
		onError: handleError,
	});

	// not_required принимает только активную причину: скрытая текущая причина остаётся лишь в «Было»
	const reason = reasons.find((item) => item.id === reasonId && item.is_active) ?? null;
	const trimmedComment = comment.trim();
	const currentReasonId = details?.reason_id ?? null;
	const currentEmployeeId = details?.person_id_federation ?? null;

	const reasonOptions = useMemo(() => reasons.filter((item) => item.is_active), [reasons]);

	const hiddenReason = reasons.find((item) => item.id === reasonId && !item.is_active);
	const hiddenReasonHint = hiddenReason
		? `Причина «${hiddenReason.name}» снята с использования — выберите другую`
		: undefined;

	const commentError =
		action === 'not_required' && reason?.comment_required && !trimmedComment
			? `Для причины «${reason.name}» комментарий обязателен`
			: null;

	const validationError = (() => {
		if (!action) {
			return 'Выберите действие';
		}

		if (action === 'found') {
			return employee ? null : 'Выберите сотрудника';
		}

		// Без справочника не проверить, активна ли причина и нужен ли комментарий
		if (isReasonsError) {
			return 'Не удалось загрузить справочник причин';
		}

		if (isReasonsPending) {
			return 'Справочник причин загружается';
		}

		if (!reason) {
			return 'Выберите причину';
		}

		return commentError;
	})();

	const isDirty = (() => {
		if (!details || !action) {
			return false;
		}

		if (action !== initialAction(details)) {
			return true;
		}

		if (action === 'found') {
			return (employee?.person_id ?? null) !== currentEmployeeId;
		}

		return reasonId !== currentReasonId || trimmedComment !== (details.guard_comment ?? '');
	})();

	const canProcess = !!details?.can_process;
	// Подтверждённую запись без изменений сохранять незачем; во время перечитывания версия может устареть
	const canSubmit =
		canProcess && !isFetching && !validationError && (isDirty || !details?.guard_confirmed);

	const handleSubmit = () => {
		if (!details || !action || !canSubmit) {
			return;
		}

		// Связку загрузки без изменений только подтверждаем: matched_by остаётся прежним.
		// confirm сервер принимает лишь для статуса 1 — статус 3 всегда сохраняется через not_required
		if (!isDirty && action === 'found' && details.match_status === 1) {
			confirm({ id: details.id, version: details.version });
			return;
		}

		update({
			id: details.id,
			payload:
				action === 'found'
					? { version: details.version, action, person_id_federation: employee?.person_id }
					: {
							version: details.version,
							action,
							reason_id: reason?.id,
							comment: trimmedComment || null,
						},
		});
	};

	/** «Было» показываем, только когда меняется связка или причина, а не один комментарий */
	const previousText = (() => {
		if (!details || !isDirty) {
			return null;
		}

		const isLinkChanged =
			action !== initialAction(details) ||
			(action === 'found' && (employee?.person_id ?? null) !== currentEmployeeId) ||
			(action === 'not_required' && reasonId !== currentReasonId);

		if (!isLinkChanged) {
			return null;
		}

		if (details.match_status === 1 && details.person_id_federation) {
			const date = changedAt(details.history, 'person_id_federation', details.person_id_federation);
			const author = [
				details.matched_by ? MATCHED_BY_TEXT[details.matched_by] : null,
				date ? formatScudDate(date) : null,
			]
				.filter(Boolean)
				.join(' ');
			const info = employeeDetails(details.federation);

			return {
				value: `${details.full_name_federation ?? details.person_id_federation}${info ? ` — ${info}` : ''}`,
				note: author,
			};
		}

		if (details.match_status === 3) {
			const date = changedAt(details.history, 'reason_id', details.reason_id);

			return {
				value: `Сопоставление не требуется — ${
					joinParts(
						reasonLabelById(reasons, details.reason_id, details.reason_name),
						details.guard_comment,
					) || SCUD_EMPTY
				}`,
				note: date ? formatScudDate(date) : '',
			};
		}

		return null;
	})();

	const previous = previousText && (
		<Text size="sm" c="dimmed">
			Было:{' '}
			<Text span size="sm" c="red.7" td="line-through">
				{previousText.value}
			</Text>
			{previousText.note && ` (${previousText.note})`}
		</Text>
	);

	const employeeCard = (value: IScudFederationPerson | null) =>
		value ? (
			<Box>
				<Text fw={700}>{value.full_name}</Text>
				<Text size="sm" c="dimmed">
					{employeeDetails(value) || SCUD_EMPTY}
				</Text>
			</Box>
		) : (
			<Text size="sm" c="dimmed">
				Сотрудник не выбран
			</Text>
		);

	const statusLine = details && (
		<Stack gap={2} align="flex-start">
			<ScudStatusBadge status={details.match_status} />
			<Text size="xs" c="dimmed">
				{details.guard_confirmed
					? `подтверждено ${formatScudDateTime(details.confirmed_at)} · ${details.confirmed_by ?? SCUD_EMPTY}`
					: 'не подтверждено'}
			</Text>
		</Stack>
	);

	return (
		<>
			<ModalSheet
				title="Редактирование пропуска"
				opened={opened}
				onClose={onClose}
				size="60rem"
				scrollAreaComponent={ScrollArea.Autosize}
			>
				<Box pos="relative" mih={200}>
					<LoadingOverlay visible={isFetching && !details} zIndex={10} />
					{isError && (
						<Alert color="red" variant="light">
							{requestErrorText(error, 'Не удалось загрузить запись')}
						</Alert>
					)}
					{details && (
						<Stack gap="md">
							<Text size="sm" c="dimmed">
								{joinParts(details.location, details.skud) || SCUD_EMPTY}
							</Text>
							<Paper withBorder radius="lg" p="sm">
								<SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
									<InfoItem label="ФИО из СКУД" value={details.full_name_skud} />
									<InfoItem
										label="Подразделение · должность (СКУД)"
										value={joinParts(details.skud_department, details.skud_post)}
									/>
									<InfoItem label="Фирма (СКУД)" value={details.skud_company} />
									<InfoItem label="Пропуск" value={scudPassNumber(details)} />
									<InfoItem
										label="Первый / последний проход"
										value={`${formatScudDate(details.first_pass)} / ${formatScudDate(details.last_pass)}`}
									/>
									<Box>
										<Text size="xs" c="dimmed">
											Текущий статус
										</Text>
										{statusLine}
									</Box>
								</SimpleGrid>
							</Paper>

							{canProcess ? (
								<Stack gap="xs">
									<Text fw={700}>Выберите действие</Text>
									<Radio.Group
										value={action}
										onChange={(value) => setAction(value as TScudMappingAction)}
									>
										<Stack gap="xs">
											<OptionCard
												value="found"
												label="Сотрудник найден"
												checked={action === 'found'}
											/>
											{action === 'found' && (
												<>
													<Paper withBorder radius="lg" p="sm">
														<Group justify="space-between" align="center" wrap="nowrap">
															{employeeCard(employee)}
															<Button
																variant="default"
																leftSection={<IconUserSearch size={16} />}
																onClick={() => setIsSearchOpened(true)}
															>
																Найти сотрудника
															</Button>
														</Group>
													</Paper>
													{previous}
												</>
											)}
											<OptionCard
												value="not_required"
												label="Сопоставление не требуется"
												checked={action === 'not_required'}
											/>
										</Stack>
									</Radio.Group>
									{action === 'not_required' && (
										<Stack gap="xs" mt="xs">
											<Radio.Group
												label="Причина"
												withAsterisk
												description={hiddenReasonHint}
												value={reasonId}
												onChange={setReasonId}
												styles={{ label: { fontWeight: 700, marginBottom: 8 } }}
											>
												<Stack gap="xs">
													{reasonOptions.map((item) => (
														<OptionCard
															key={item.id}
															value={item.id}
															label={reasonLabel(item)}
															checked={reasonId === item.id}
														/>
													))}
												</Stack>
											</Radio.Group>
											<LabelInput
												compact
												multiple
												label="Комментарий"
												minRows={2}
												maxRows={6}
												maxLength={SCUD_COMMENT_MAX_LENGTH}
												value={comment}
												onChange={(event) => setComment(event.currentTarget.value)}
												error={commentError}
												description={`До ${SCUD_COMMENT_MAX_LENGTH} символов.`}
												inputWrapperOrder={['label', 'input', 'error', 'description']}
											/>
											{previous}
										</Stack>
									)}
								</Stack>
							) : (
								<Stack gap="xs">
									<Alert color="gray" variant="light">
										Нет права обработки записей локации «{details.location ?? SCUD_EMPTY}» — только
										просмотр
									</Alert>
									{details.match_status === 1 && employeeCard(initialEmployee(details))}
									{details.match_status === 3 && (
										<Text size="sm">
											{joinParts(
												reasonLabelById(reasons, details.reason_id, details.reason_name),
												details.guard_comment,
											) || SCUD_EMPTY}
										</Text>
									)}
								</Stack>
							)}

							<Text fw={700}>История изменений</Text>
							<ScudMappingHistory history={details.history} reasons={reasons} />
						</Stack>
					)}
				</Box>
				<Group justify="flex-end" gap="sm">
					<Button variant="outline" onClick={onClose}>
						Отмена
					</Button>
					{canProcess && (
						<Button
							disabled={!canSubmit}
							loading={isConfirming || isUpdating}
							onClick={handleSubmit}
							title={canSubmit ? undefined : (validationError ?? 'Изменений нет')}
						>
							Подтвердить
						</Button>
					)}
				</Group>
			</ModalSheet>
			<FederationSearchModal
				opened={opened && isSearchOpened}
				onClose={() => setIsSearchOpened(false)}
				mapping={details}
				onSelect={(selected) => {
					setEmployee(selected);
					setAction('found');
					setIsSearchOpened(false);
				}}
			/>
		</>
	);
};
